import {
  JsonFormControl,
  makeCommentsControl,
  makeDestinationSelectControl,
  makeNumberControl,
  makeOrientationSelectControl,
  makePassFailControl,
  makeSelectControl,
  makeSerialNumberAutoselect,
  makeSerialNumberSelect,
  makeSerialNumberText,
} from "@components";

/**
 * This file contains all Form Control objects for all **PS** Module production
 * steps. Form conotrol objects added here should be exported to the `index`
 * file, and added as a switch condition in `getFormControl`.
 */

const SENSOR_VISUAL_INSPECTION: JsonFormControl[] = [
  makeSerialNumberText("PS.Sensor", "Sensor Serial Number", "sensorSerial"),
  makePassFailControl("Visual Inspection"),
  {
    label: "Image",
    inputType: "multipleimage",
    fieldName: "multipleimage",
    defaultValue: undefined,
  },
  makeCommentsControl(),
];

const GLUE_HV_TAIL_TO_SENSOR: JsonFormControl[] = [
  makeSerialNumberSelect("PS.Sensor", "Sensor Serial Number", "sensorSerial"),
  makeSerialNumberSelect(
    "Aux.Fixture.PS HVTailGluing",
    "HV Tail Gluing Fixture",
    "hvTailGluingFixture",
  ),
  makeSerialNumberSelect("Aux.Glue.EP_601_LV", "EP_601_LV Glue", "ep601LvGlue"),
  makeSelectControl(
    "Sensor Orientation",
    "orientation",
    [
      { value: "normal", label: "Normal" },
      { value: "rotated", label: "Rotated" },
    ],
    "Please select a sensor orientation.",
  ),
  makeCommentsControl(),
];

const ENCAPSULATE_HV_TAIL: JsonFormControl[] = [
  makeSerialNumberSelect("PS.Sensor", "Sensor Serial Number", "sensorSerial"),
  makeSerialNumberSelect(
    "Aux.Fixture.PS HVTailWirebond",
    "HV Tail Wirebond Fixture",
    "hvTailWirebondFixture",
  ),
  {
    label: "Wirebond Program ID",
    inputType: "text",
    fieldName: "wirebondProgramId",
    defaultValue: "",
    config: {
      required: "Program ID is required.",
    },
  },
  makeSerialNumberSelect(
    "Aux.Encapsulant.Slyguard_186",
    "Slyguard 186 Encapsulant",
    "slyguard186Encapsulant",
  ),
  makeCommentsControl(),
];

const SENSOR_IV_MEASURMENT: JsonFormControl[] = [
  makeSerialNumberSelect("PS.Sensor", "Sensor Serial Number", "sensorSerial"),
  // TODO: File input
  makePassFailControl("IV Measurement Result"),
  {
    label: "Image",
    inputType: "image",
    fieldName: "image",
    defaultValue: undefined,
  },
  makeCommentsControl(),
];

const VTRX_VISUAL_INSPECTION: JsonFormControl[] = [
  makeSerialNumberText("PS.VTRX", "VTRX Serial", "vtrxSerial"),
  makePassFailControl("Visual Inspection"),
  {
    label: "Image",
    inputType: "multipleimage",
    fieldName: "multipleimage",
    defaultValue: undefined,
  },
  makeCommentsControl(),
];

const MA_PSA_VISUAL_INSPECTION: JsonFormControl[] = [
  makeSerialNumberText("PS.MaPSA", "MaPSA Serial", "mapsaSerial"),
  makePassFailControl("Visual Inspection"),
  {
    label: "Image",
    inputType: "multipleimage",
    fieldName: "multipleimage",
    defaultValue: undefined,
  },
  makeCommentsControl(),
];

const TEST_MA_PSA: JsonFormControl[] = [
  makeSerialNumberSelect("PS.MaPSA", "MaPSA Serial", "mapsaSerial"),
  // Replaced makePassFailControl with makeSelectControl for new grades
  makeSelectControl(
    "MaPSA Test Result",
    "mapsaTestResult",
    [
      { value: "A", label: "A" },
      { value: "B", label: "B" },
      { value: "C", label: "C" },
    ],
    "Please select a MaPSA test grade.",
  ),
  makeCommentsControl(),
];

const CF_BASEPLATE_INSERT_GLUING: JsonFormControl[] = [
  makeSerialNumberAutoselect(
    "PS.CFBaseplate",
    "CF Baseplate Serial",
    "cfBasplateSerial",
  ),
  makeSerialNumberSelect(
    "Aux.Fixture.InsertGluing",
    "Insert Gluing Serial",
    "insertGluingFixture",
  ),
  makeSerialNumberSelect("Aux.Glue.EP_601_LV", "EP_601 Epoxy", "ep601Epoxy"),
  makeCommentsControl(),
];

const CF_BASEPLATE_KAPTON_GLUING: JsonFormControl[] = [
  makeSerialNumberSelect(
    "PS.CFBaseplate",
    "CF Baseplate Serial",
    "cfBasplateSerial",
  ),
  makeSerialNumberSelect(
    "Aux.Fixture.PS KaptonGluing",
    "Kapton Gluing Fixture",
    "kaptonGluingFixture",
  ),
  {
    label: "Dispensing Program",
    inputType: "text",
    fieldName: "dispensingProgram",
    defaultValue: "",
    config: {
      required: "Dispensing program ID is required.",
    },
  },
  makeSerialNumberSelect("Aux.Glue.EP_601_LV", "EP_601 Epoxy", "ep601Epoxy"),
  makeCommentsControl(),
];

const MA_PSA_BASEPLATE_GLUING: JsonFormControl[] = [
  makeSerialNumberSelect("PS.MaPSA", "MaPSA Serial", "mapsaSerial"),
  makeSerialNumberSelect(
    "PS.CFBaseplate",
    "CF Baseplate Serial",
    "cfBasplateSerial",
  ),
  makeSerialNumberSelect("Aux.Glue.EP_601_LV", "EP_601 Epoxy", "ep601Epoxy"),
  makeCommentsControl(),
];

const SENSOR_SPACER_GLUING: JsonFormControl[] = [
  /**
   * PS-s sensor ID
   * Spacer IDs (optional?)
   * EP_601 epoxy used
   * Fast-cure epoxy used
   * Comments (Optional)
   */
  makeSerialNumberSelect("PS.Sensor", "Sensor Serial Number", "sensorSerial"),
  makeSelectControl(
    "Spacer Thickness",
    "thickness",
    ["1.6mm", "2.6mm", "4.0mm"],
    "Please select a thickness",
  ),
  {
    label: "Spacer IDs",
    inputType: "text",
    fieldName: "spaceIds",
    defaultValue: "",
  },
  makeSerialNumberSelect("Aux.Glue.EP_601_LV", "EP_601 Epoxy", "ep601Epoxy"),
  {
    label: "Fast Cure Epoxy",
    inputType: "text",
    fieldName: "fastCureEpoxy",
    defaultValue: "",
    config: {
      required: "Please enter a Fast Cure Epoxy",
    },
  },
  makeCommentsControl(),
];

const SENSOR_BASEPLATE_GLUING: JsonFormControl[] = [
  makeSerialNumberText("PS.Module", "Sandwich Serial Number", "moduleSerial"),
  makeSerialNumberSelect("PS.Sensor", "Sensor Serial Number", "sensorSerial"),
  makeSerialNumberSelect("PS.MaPSA", "MaPSA Serial", "mapsaSerial"),
  makeSerialNumberSelect("Aux.Glue.EP_601_LV", "EP_601 Epoxy", "ep601Epoxy"),
  {
    label: "Fast Cure Epoxy",
    inputType: "text",
    fieldName: "fastCureEpoxy",
    defaultValue: "",
    config: {
      required: "Please enter a Fast Cure Epoxy",
    },
  },
  makeSerialNumberSelect(
    "Aux.Fixture.PS ModuleCarrier",
    "Module Carrier Serial Number",
    "moduleCarrier",
  ),
  makeCommentsControl(),
];

const SANDWICH_VISUAL_INSPECTION: JsonFormControl[] = [
  makeSerialNumberSelect("PS.Module", "Sandwich Serial Number", "moduleSerial"),
  makePassFailControl("Visual Inspection"),
  {
    label: "Image",
    inputType: "multipleimage",
    fieldName: "multipleimage",
    defaultValue: undefined,
  },
  makeCommentsControl(),
];

const TEST_SANDWICH_METROLOGY: JsonFormControl[] = [
  makeSerialNumberSelect("PS.Module", "Sandwich Serial Number", "moduleSerial"),
  makeSerialNumberSelect(
    "Aux.Fixture.Metrology",
    "Metrology Fixture",
    "metrologyFixture",
  ),
  // Rotation angle (urad)
  {
    label: "Rotation Angle (urad)",
    inputType: "number",
    fieldName: "rotationAngle",
    defaultValue: "",
    config: {
      required: "Enter a rotation angle in urad.",
    },
  },
  // Sensor parallel offset (um)
  {
    label: "Sensor Parallel Offset (um)",
    fieldName: "sensorParallelOffset",
    inputType: "number",
    defaultValue: "",
    config: {
      required: "Enter a sensor parallel offset in um.",
    },
  },
  // Sensor perpendicular offset (um)
  {
    label: "Sensor Perpendicular Offset (um)",
    fieldName: "sensorPerpendicularOffset",
    inputType: "number",
    defaultValue: "",
    config: {
      required: "Enter a sensor perpendicular offset in um.",
    },
  },
  makeSelectControl(
    "MaPSA Orientation",
    "mapsaOrientation",
    [
      { value: "normal", label: "Normal" },
      { value: "rotated", label: "Rotated" },
    ],
    "Please select a sensor orientation.",
  ),
  makeSelectControl(
    "PSS Sensor Orientation",
    "pssOrientation",
    [
      { value: "normal", label: "Normal" },
      { value: "rotated", label: "Rotated" },
    ],
    "Please select a sensor orientation.",
  ),
  {
    label: "PS Sensor Scratch Pad (autofilled)",
    inputType: "text",
    fieldName: "scratchpadPS",
    defaultValue: "Module not selected or no scratchpad info for this module",
    dependency: "sandwichSerial",
  },
  {
    label: "PS Sensor Scratchpad Image",
    inputType: "multipleimage",
    fieldName: "multipleimage",
    defaultValue: undefined,
  },
  makeCommentsControl(),
];

const FEH_VISUAL_INSPECTION: JsonFormControl[] = [
  makeSerialNumberText("PS.FEH", "FEH Serial Number", "fehSerial"),
  // Thickness?
  makePassFailControl("Visual Inspection"),
  {
    label: "Image",
    inputType: "multipleimage",
    fieldName: "multipleimage",
    defaultValue: undefined,
  },
  makeCommentsControl(),
];

const POH_VISUAL_INSPECTION: JsonFormControl[] = [
  makeSerialNumberText("PS.POH", "POH Serial Number", "pohSerial"),
  makePassFailControl("Visual Inspection"),
  {
    label: "Image",
    inputType: "multipleimage",
    fieldName: "multipleimage",
    defaultValue: undefined,
  },
  makeCommentsControl(),
];

const ROH_VISUAL_INSPECTION: JsonFormControl[] = [
  makeSerialNumberText("PS.ROH", "ROH Serial Number", "rohSerial"),
  // Thickness?
  makeSelectControl(
    "ROH Bandwidth",
    "bandwidth",
    ["5G", "10G"],
    "Please select a bandwidth.",
  ),
  makePassFailControl("Visual Inspection"),
  {
    label: "Image",
    inputType: "multipleimage",
    fieldName: "multipleimage",
    defaultValue: undefined,
  },
  makeCommentsControl(),
];

const SANDWICH_HYBRID_GLUING: JsonFormControl[] = [
  makeSerialNumberSelect("PS.Module", "Sandwich Serial Number", "moduleSerial"),
  makeSerialNumberSelect("PS.FEH", "Left FEH Serial Number", "fehLeftSerial"),
  makeSerialNumberSelect("PS.FEH", "Right FEH Serial Number", "fehRightSerial"),
  makeSerialNumberSelect("PS.POH", "POH Serial Number", "pohSerial"),
  {
    label: "Incomplete Module (No ROH)?",
    options: [
      { label: "Incomplete", value: "yes" },
      { label: "Complete", value: "no" },
    ],
    inputType: "select",
    defaultValue: "Complete",
    fieldName: "isIncomplete",
  },
  makeSerialNumberSelect(
    "PS.ROH",
    "ROH Serial Number",
    "rohSerial",
    undefined,
    [
      {
        depFieldName: "isIncomplete",
        depFieldValue: "yes",
        depFieldValueCondition: "=",
      },
    ],
  ),
  makeSelectControl(
    "Module Thickness",
    "thickness",
    ["1.6mm", "2.6mm", "4.0mm"],
    "Please select a thickness",
  ),
  makeSerialNumberSelect(
    "Aux.Fixture.PS HybridGluing",
    "Hybrid Gluing Fixture",
    "hybridGluingFixture",
  ),
  makeSerialNumberSelect("Aux.Glue.TC_437", "TC 437 Glue", "tc437Glue"),
  makeCommentsControl(),
];

const AUX_PS_EXTRA_GLUING: JsonFormControl[] = [
  makeSerialNumberSelect("PS.Module", "Sandwich Serial Number", "moduleSerial"),
  makeSerialNumberSelect(
    "Aux.Fixture.PS HybridGluing",
    "Hybrid Gluing Fixture",
    "hybridGluingFixture",
  ),
  makeSerialNumberSelect("PS.ROH", "ROH Serial Number", "rohSerial"),
  makeCommentsControl(),
];

const MODULE_PRE_SHIP_VISUAL_INSPECTION: JsonFormControl[] = [
  makeSerialNumberSelect("PS.Module", "Sandwich Serial Number", "moduleSerial"),
  makePassFailControl("Visual Inspection"),
  {
    label: "Image",
    inputType: "multipleimage",
    fieldName: "multipleimage",
    defaultValue: undefined,
  },
  makeCommentsControl(),
];

const PACK_AND_SHIP_MODULE: JsonFormControl[] = [
  makeSerialNumberSelect("PS.Module", "Sandwich Serial Number", "moduleSerial"),
  makeDestinationSelectControl("Shipping Destination", "destination"),
  makeCommentsControl(),
];

const RETRIEVE_AND_INSPECT_MODULE: JsonFormControl[] = [
  makeSerialNumberSelect("PS.Module", "Sandwich Serial Number", "moduleSerial"),
  makePassFailControl("Visual Inspection"),
  makeCommentsControl(),
];

const FIRST_SENSOR_WIREBOND: JsonFormControl[] = [
  makeSerialNumberSelect("PS.Module", "Sandwich Serial Number", "moduleSerial"),
  makeSelectControl(
    "Top or bottom sensor?",
    "whichSensor",
    [
      { value: "top", label: "Top" },
      { value: "bottom", label: "Bottom" },
    ],
    "Please select a sensor.",
  ),
  makeSerialNumberSelect(
    "Aux.Fixture.PS ModuleWirebond",
    "Wirebond Fixture",
    "wirebondFixture",
  ),
  {
    label: "Wirebond Program ID",
    inputType: "text",
    fieldName: "wirebondProgramId",
    defaultValue: "",
    config: {
      required: "Please input a Program ID",
    },
  },
  makeCommentsControl(),
];

const SECOND_SENSOR_WIREBOND: JsonFormControl[] = [...FIRST_SENSOR_WIREBOND];

const TOP_SENSOR_WIREBOND: JsonFormControl[] = [...FIRST_SENSOR_WIREBOND];

const BOTTOM_SENSOR_WIREBOND: JsonFormControl[] = [...FIRST_SENSOR_WIREBOND];

const ATTACH_VTRX: JsonFormControl[] = [
  makeSerialNumberSelect("PS.Module", "Sandwich Serial Number", "moduleSerial"),
  {
    label: "Is VTRX attached?",
    options: [
      { label: "Yes", value: "yes" },
      { label: "No", value: "no" },
    ],
    inputType: "select",
    defaultValue: "No",
    fieldName: "isVTRXAttached",
  },
  makeSerialNumberSelect(
    "PS.VTRX",
    "VTRx Serial Number",
    "vtrxSerial",
    undefined,
    [
      {
        depFieldName: "isVTRXAttached",
        depFieldValue: "yes",
        depFieldValueCondition: "=",
      },
    ],
  ),
  makeCommentsControl(),
];

const PRE_ENCAPSULATION_TEST: JsonFormControl[] = [
  makeSerialNumberSelect("PS.Module", "Sandwich Serial Number", "moduleSerial"),
  makeOrientationSelectControl(
    "KIT Box Orientation",
    "kitBox",
    "Please select a KIT Box orientation",
  ),
  // Test File
  makePassFailControl("Pre Encapsulation Test"),
  makeSelectControl(
    "Any bonding issues",
    "bondingissues",
    ["Yes", "No"],
    "Please select a value",
  ),
  makeNumberControl(
    "Number of top left side defective bonds",
    "topLeftDefectNumber",
    "Please enter a value",
    undefined,
    [
      {
        depFieldName: "bondingissues",
        depFieldValue: "No",
        depFieldValueCondition: "=",
      },
    ],
  ),
  {
    label: "Top left side defective bond list",
    inputType: "text",
    fieldName: "topLeftDefectString",
    defaultValue: "",
  },
  makeNumberControl(
    "Number of top right right defective bonds",
    "topRightDefectNumber",
    "Please enter a value",
    undefined,
    [
      {
        depFieldName: "bondingissues",
        depFieldValue: "No",
        depFieldValueCondition: "=",
      },
    ],
  ),
  {
    label: "Top right side defective bond list",
    inputType: "text",
    fieldName: "topRightDefectString",
    defaultValue: "",
  },
  makeNumberControl(
    "Number of bottom left side defective bonds",
    "bottomLeftDefectNumber",
    "Please enter a value",
    undefined,
    [
      {
        depFieldName: "bondingissues",
        depFieldValue: "No",
        depFieldValueCondition: "=",
      },
    ],
  ),
  {
    label: "Bottom left side defective bond list",
    inputType: "text",
    fieldName: "bottomLeftDefectString",
    defaultValue: "",
  },
  makeNumberControl(
    "Number of bottom right side defective bonds",
    "bottomRightDefectNumber",
    "Please enter a value",
    undefined,
    [
      {
        depFieldName: "bondingissues",
        depFieldValue: "No",
        depFieldValueCondition: "=",
      },
    ],
  ),
  {
    label: "Bottom right side defective bond list",
    inputType: "text",
    fieldName: "bottomRightDefectString",
    defaultValue: "",
  },
  makeCommentsControl(),
];

const FIRST_SENSOR_ENCAPSULATE: JsonFormControl[] = [
  makeSerialNumberSelect("PS.Module", "Sandwich Serial Number", "moduleSerial"),
  makeSelectControl(
    "Top or bottom sensor?",
    "whichSensor",
    [
      { value: "top", label: "Top" },
      { value: "bottom", label: "Bottom" },
    ],
    "Please select a sensor.",
  ),
  makeSerialNumberSelect(
    "Aux.Fixture.Encapsulation",
    "Encapsulation Fixture",
    "encapsulationFixture",
  ),
  makeSerialNumberSelect(
    "Aux.Encapsulant.Slyguard_186",
    "Slyguard 186",
    "slyguard186",
  ),
  makeCommentsControl(),
];

const SECOND_SENSOR_ENCAPSULATE: JsonFormControl[] = [
  ...FIRST_SENSOR_ENCAPSULATE,
];

const TOP_SENSOR_ENCAPSULATE: JsonFormControl[] = [...FIRST_SENSOR_ENCAPSULATE];

const BOTTOM_SENSOR_ENCAPSULATE: JsonFormControl[] = [
  ...FIRST_SENSOR_ENCAPSULATE,
];

const POST_ENCAPSULATION_VISUAL_INSPECTION: JsonFormControl[] = [
  makeSerialNumberSelect("PS.Module", "Sandwich Serial Number", "moduleSerial"),
  makePassFailControl("Visual Inspection"),
  {
    label: "Image",
    inputType: "multipleimage",
    fieldName: "multipleimage",
    defaultValue: undefined,
  },
  makeSelectControl(
    "KIT box orientation",
    "kitboxorientation",
    ["A", "B"],
    "Please select an orientation",
  ),
  makePassFailControl("Pass/fail grade", "passfailgrade"),
  makeSelectControl(
    "Potato grade",
    "potatograde",
    ["A", "B", "C"],
    "Please select a grade",
  ),
  makeCommentsControl(),
];

const BURN_IN: JsonFormControl[] = [
  makeSerialNumberSelect("PS.Module", "Sandwich Serial Number", "moduleSerial"),
  makeOrientationSelectControl(
    "Burn In Box Side",
    "burnInBoxSide",
    "Please select a Burn In Box side.",
  ),
  makeSelectControl(
    "Burn In Box Slot",
    "burnInBoxSlot",
    ["1", "2", "3", "4", "5"],
    "Please select a Burn In Box Slot",
  ),
  makePassFailControl("Burn In Test"),
  // Test File
  makeCommentsControl(),
];

const POST_BURN_IN_VISUAL_INSPECTION: JsonFormControl[] = [
  makeSerialNumberSelect("PS.Module", "Sandwich Serial Number", "moduleSerial"),
  makePassFailControl("Visual Inspection"),
  {
    label: "Image",
    inputType: "multipleimage",
    fieldName: "multipleimage",
    defaultValue: undefined,
  },
  makeCommentsControl(),
];

const FINAL_PACK_AND_SHIP: JsonFormControl[] = [
  makeSerialNumberSelect("PS.Module", "Sandwich Serial Number", "moduleSerial"),
  makeDestinationSelectControl("Shipping Destination", "destination", true),
  makeCommentsControl(),
];

const StepsPS: { [key: string]: JsonFormControl[] } = {
  SENSOR_VISUAL_INSPECTION,
  GLUE_HV_TAIL_TO_SENSOR,
  ENCAPSULATE_HV_TAIL,
  SENSOR_IV_MEASURMENT,
  MA_PSA_VISUAL_INSPECTION,
  TEST_MA_PSA,
  VTRX_VISUAL_INSPECTION,
  CF_BASEPLATE_INSERT_GLUING,
  CF_BASEPLATE_KAPTON_GLUING,
  MA_PSA_BASEPLATE_GLUING,
  SENSOR_SPACER_GLUING,
  SENSOR_BASEPLATE_GLUING,
  SANDWICH_VISUAL_INSPECTION,
  TEST_SANDWICH_METROLOGY,
  FEH_VISUAL_INSPECTION,
  POH_VISUAL_INSPECTION,
  ROH_VISUAL_INSPECTION,
  SANDWICH_HYBRID_GLUING,
  MODULE_PRE_SHIP_VISUAL_INSPECTION,
  PACK_AND_SHIP_MODULE,
  RETRIEVE_AND_INSPECT_MODULE,
  FIRST_SENSOR_WIREBOND,
  SECOND_SENSOR_WIREBOND,
  TOP_SENSOR_WIREBOND,
  BOTTOM_SENSOR_WIREBOND,
  ATTACH_VTRX,
  PRE_ENCAPSULATION_TEST,
  FIRST_SENSOR_ENCAPSULATE,
  SECOND_SENSOR_ENCAPSULATE,
  TOP_SENSOR_ENCAPSULATE,
  BOTTOM_SENSOR_ENCAPSULATE,
  POST_ENCAPSULATION_VISUAL_INSPECTION,
  BURN_IN,
  POST_BURN_IN_VISUAL_INSPECTION,
  FINAL_PACK_AND_SHIP,
};

const StepsPSAux: { [key: string]: JsonFormControl[] } = {
  AUX_PS_EXTRA_GLUING,
};

export { StepsPS, StepsPSAux };
